import { useParams, Link } from "react-router-dom";
import DocumentMeta from "@/components/base/DocumentMeta";
import SchemaOrg from "@/components/base/SchemaOrg";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import { getArticleBySlug, getRelatedArticles } from "@/mocks/articles";
import { getCategoryBySlug } from "@/mocks/categories";
import { useEffect } from "react";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || "");
  const category = article ? getCategoryBySlug(article.categorySlug) : undefined;
  const relatedArticles = article ? getRelatedArticles(article.slug, article.categorySlug) : [];

  const siteUrl = "https://worldcup-tickets-guide.com";
  const articleUrl = `${siteUrl}/article/${slug}`;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 px-4 text-center">
          <h1 className="text-2xl font-bold text-foreground-950">文章未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block cursor-pointer">
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const renderContent = (content: typeof article.content) => {
    return content.map((block, idx) => {
      switch (block.type) {
        case "heading":
          const HeadingTag = `h${block.level || 2}` as keyof JSX.IntrinsicElements;
          return (
            <HeadingTag
              key={idx}
              className={`font-bold text-foreground-950 mt-8 mb-3 ${
                block.level === 2 ? "text-xl md:text-2xl" : "text-lg md:text-xl"
              }`}
            >
              {block.text}
            </HeadingTag>
          );
        case "paragraph":
          return (
            <p key={idx} className="text-foreground-700 leading-relaxed mb-4 text-sm md:text-base">
              {block.text}
            </p>
          );
        case "list":
          return (
            <ul key={idx} className="space-y-2 mb-4 pl-5">
              {block.items?.map((item, i) => (
                <li key={i} className="text-foreground-700 text-sm md:text-base flex items-start gap-2">
                  <span className="text-primary-500 mt-0.5 shrink-0">&bull;</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          );
        case "tip":
          return (
            <div key={idx} className="bg-accent-100 border-l-4 border-accent-500 rounded-r-lg p-4 mb-4">
              <div className="flex items-start gap-2">
                <i className="ri-lightbulb-line text-accent-500 mt-0.5 shrink-0"></i>
                <p className="text-foreground-700 text-sm leading-relaxed">{block.text}</p>
              </div>
            </div>
          );
        case "warning":
          return (
            <div key={idx} className="bg-red-50 border-l-4 border-red-400 rounded-r-lg p-4 mb-4">
              <div className="flex items-start gap-2">
                <i className="ri-error-warning-line text-red-500 mt-0.5 shrink-0"></i>
                <p className="text-foreground-700 text-sm leading-relaxed">{block.text}</p>
              </div>
            </div>
          );
        default:
          return null;
      }
    });
  };

  return (
    <>
      <DocumentMeta
        lang="zh-CN"
        title={`${article.title} - 世界杯票务指南`}
        description={article.summary}
        keywords={article.tags.join(",")}
        canonical={articleUrl}
        ogTitle={article.title}
        ogDescription={article.summary}
        ogType="article"
        ogUrl={articleUrl}
        ogSiteName="世界杯票务指南"
        ogLocale="zh_CN"
        twitterCard="summary_large_image"
        twitterTitle={article.title}
        twitterDescription={article.summary}
      />

      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.summary,
          url: articleUrl,
          datePublished: article.publishDate,
          author: { "@type": "Person", name: article.author },
          publisher: {
            "@type": "Organization",
            name: "世界杯票务指南",
          },
          image: article.coverImage,
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: siteUrl },
            {
              "@type": "ListItem",
              position: 2,
              name: category?.name || "分类",
              item: `${siteUrl}/category/${article.categorySlug}`,
            },
            { "@type": "ListItem", position: 3, name: article.title },
          ],
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <article className="px-4 md:px-6 max-w-3xl mx-auto pb-12">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: category?.name || "分类", href: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />

            <header className="mt-6 mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                {category && (
                  <Link
                    to={`/category/${article.categorySlug}`}
                    className="text-xs px-2.5 py-1 bg-secondary-100 text-secondary-700 rounded-full font-medium whitespace-nowrap hover:bg-secondary-200 transition-colors cursor-pointer"
                  >
                    {category.name}
                  </Link>
                )}
                <span className="text-xs text-foreground-500 whitespace-nowrap">
                  {article.publishDate}
                </span>
                <span className="text-xs text-foreground-400 whitespace-nowrap">
                  {article.readTime} 分钟阅读
                </span>
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>

              <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                {article.summary}
              </p>

              <div className="mt-4 flex items-center gap-2">
                <span className="text-xs text-foreground-500">作者：{article.author}</span>
              </div>
            </header>

            <div className="relative h-64 md:h-80 rounded-lg overflow-hidden mb-8">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="prose-custom">
              {renderContent(article.content)}
            </div>

            <div className="mt-8 pt-6 border-t border-background-200">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1 bg-background-100 text-foreground-600 rounded-full whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="bg-background-100 py-12">
              <div className="px-4 md:px-6 max-w-3xl mx-auto">
                <h2 className="text-xl font-bold text-foreground-950 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.slug}`}
                      className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative h-36 overflow-hidden">
                        <img
                          src={rel.coverImage}
                          alt={rel.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-3">
                        <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                          {rel.title}
                        </h3>
                        <span className="text-xs text-foreground-500 mt-1 block">
                          {rel.publishDate}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}